/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiFieldText,
  EuiFormRow,
  EuiCheckbox,
  EuiSpacer,
  EuiText,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

/* ==========================================================================
   CONSTANTS & DOMAIN TYPES
   ========================================================================== */

export interface AwsState {
  /** The unique 12-digit identifier for the AWS account. */
  account_id: string
  /** Whether to retroactively charge for consumption in the current month. */
  backdate_on_consumption: boolean
}

interface Props {
  values: AwsState
  touched: Partial<Record<keyof AwsState, boolean>>
  isDisabled: boolean
  onChange: (updates: Partial<AwsState>) => void
  onBlur: (field: keyof AwsState) => void
  showBackdate: boolean
}

/* ==========================================================================
   COMPONENT DEFINITION
   ========================================================================== */

const AwsProviderForm = ({
  values,
  touched,
  isDisabled,
  onChange,
  onBlur,
  showBackdate,
}: Props) => {
  // --------------------------------------------------------------------------
  // State & Logic
  // --------------------------------------------------------------------------

  const isInvalid = touched.account_id && !values.account_id.trim()

  // Accessibility: Generate a unique ID for the checkbox label
  const checkboxId = useGeneratedHtmlId({ prefix: 'aws-backdate' })

  // --------------------------------------------------------------------------
  // Main Render
  // --------------------------------------------------------------------------

  return (
    <React.Fragment>
      {/* SECTION: IDENTITY
        Links the Elastic Organization to AWS.
      */}
      <EuiFormRow
        label={<FormattedMessage id='aws.account-id' defaultMessage='AWS Account ID' />}
        isInvalid={isInvalid}
        error={
          isInvalid ? (
            <FormattedMessage
              id='aws.account-id-is-required'
              defaultMessage='AWS Account ID is required'
            />
          ) : undefined
        }
        helpText={
          <FormattedMessage
            id='change-domain-flyout.account-id.help-text'
            defaultMessage='The AWS Account ID to attach to this organization.'
          />
        }
        fullWidth={true}
      >
        <EuiFieldText
          data-test-id='aws-account-id-input'
          value={values.account_id}
          icon='tokenAlias'
          onChange={(e) => onChange({ account_id: e.target.value })}
          onBlur={() => onBlur('account_id')}
          readOnly={isDisabled}
          isInvalid={isInvalid}
          placeholder='e.g. 123456789012'
          fullWidth={true}
        />
      </EuiFormRow>

      {/* SECTION: BILLING CONTINUITY (Conditional)
        Prevents usage gaps during domain migration.
      */}
      {showBackdate && (
        <React.Fragment>
          <EuiSpacer size='m' />

          <EuiFormRow
            helpText={
              <FormattedMessage
                id='aws.backdate_on_consumption.help-text'
                defaultMessage={
                  'Check this only if the customer has EXPLICITLY approved ' +
                  'Elastic to charge outstanding usage accumulated over this ' +
                  'month to their new AWS marketplace subscription. This will ' +
                  'result in an increased one-time consolidated charge on ' +
                  'the initial AWS invoice covering the transition period.'
                }
              />
            }
          >
            <EuiCheckbox
              data-test-id='aws-retroactive-billing-checkbox'
              id={checkboxId}
              label={
                <EuiText size='s'>
                  <strong>
                    <FormattedMessage
                      id='billing.backdate'
                      defaultMessage='Enable retroactive consumption billing'
                    />
                  </strong>
                </EuiText>
              }
              checked={values.backdate_on_consumption}
              onChange={(e) => onChange({ backdate_on_consumption: e.target.checked })}
              disabled={isDisabled}
            />
          </EuiFormRow>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default AwsProviderForm
